import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { session } from '../../client/session.js';
import { router } from '../../shared/router.js';
import { escapeHtml, stripHtml } from '../../shared/utils.js';
import componentStyles from './auth-form.css?inline';

export class AuthForm extends BaseComponent {
  private mode: 'login' | 'signup' = 'login';
  private loading = false;
  private error = '';

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    const isSignup = this.mode === 'signup';

    return `
    <div class="auth-container">
      <header class="header">
        <div class="logo">Agentor</div>
      </header>
      <div class="auth-form">
        <ag-panel>
          <ag-panel-header>
            <h2>${isSignup ? 'Create Account' : 'Welcome Back'}</h2>
          </ag-panel-header>
          <ag-panel-content>
            ${this.error ? `<div class="error">${escapeHtml(this.error)}</div>` : ''}
            
            <form id="form" autocomplete="on">
              ${isSignup ? `
                <ag-form-group>
                  <label for="name">Name</label>
                  <input type="text" id="name" name="name" required autocomplete="name">
                </ag-form-group>
                <ag-form-group>
                  <label for="accountType">Account Type</label>
                  <select id="accountType" name="accountType">
                    <option value="personal">Personal</option>
                    <option value="company">Company</option>
                  </select>
                </ag-form-group>
                <ag-form-group class="company-field" id="companyField">
                  <label for="companyName">Company Name</label>
                  <input type="text" id="companyName" name="companyName" autocomplete="organization">
                </ag-form-group>
              ` : ''}
              
              <ag-form-group>
                <label for="email">Email</label>
                <input type="email" id="email" name="email" required autocomplete="email">
              </ag-form-group>
              
              <ag-form-group>
                <label for="password">Password</label>
                <input type="password" id="password" name="password" required minlength="6">
              </ag-form-group>
              
              <button type="submit" ${this.loading ? 'disabled' : ''}>
                ${this.loading ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Log In')}
              </button>
            </form>
            
            <div class="mode-switcher">
              ${isSignup ? 'Already have an account? <a id="toggleMode">Log in</a>'
        : "Don't have an account? <a id=\"toggleMode\">Sign up</a>"}
            </div>
            ${!isSignup ? `<div class="mode-switcher"><a href="/forgot-password">Forgot password?</a></div>` : ''}
          </ag-panel-content>
        </ag-panel>
      </div>
    </div>
    `;
  }

  protected afterRender(): void {
    const form = this.$('#form') as HTMLFormElement;
    const toggleBtn = this.$('#toggleMode');
    const accountType = this.$('#accountType') as HTMLSelectElement;
    const companyField = this.$('#companyField');

    form?.addEventListener('submit', (e) => this.handleSubmit(e));
    toggleBtn?.addEventListener('click', () => this.toggleMode());

    accountType?.addEventListener('change', () => {
      if (companyField) {
        companyField.classList.toggle('visible', accountType.value === 'company');
      }
    });
  }

  private toggleMode(): void {
    this.mode = this.mode === 'login' ? 'signup' : 'login';
    this.error = '';
    this.render();
    this.afterRender();
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = this.$('#form') as HTMLFormElement;
    const formData = new FormData(form);

    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    this.loading = true;
    this.error = '';
    this.render();
    this.afterRender();

    try {
      let result;

      if (this.mode === 'signup') {
        const name = stripHtml(formData.get('name') as string);
        const accountType = formData.get('accountType') as 'personal' | 'company';
        const companyName = stripHtml(formData.get('companyName') as string);

        result = await api.register(email, password, name, accountType, companyName);
      } else {
        result = await api.login(email, password);
      }

      if (result.success && result.data) {
        session.setUser(result.data.user);
        router.navigate('/dashboard');
      } else {
        this.error = result.error || 'Authentication failed';
      }
    } catch (err) {
      this.error = (err as Error).message;
    }

    this.loading = false;
    this.render();
    this.afterRender();
  }
}

customElements.define('auth-form', AuthForm);
